import { isNotEmpty, useForm } from '@mantine/form'
import { useDispatch, useSelector } from 'react-redux'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { useEffect, useState } from 'react'
import { LoanParams } from 'app/models/loan-params'
import {
  SelectItem,
} from '@mantine/core'
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid'
import {
  rateTypeOptions,
  Option,
} from 'app/models/dropdown-options'
import {
  formatDateToUTC,
  roundTo, stringToDate,
  toNumberInputValue,
  toOptionalFxRate,
  validateOptionalFxRate,
} from 'app/utils/util-functions'
import {
  loadAllLoanRollovers,
  saveLoanRollover,
  submitForApproval,
} from 'app/state/ducks/loan-rollovers/thunks'
import { getLoanRollovers } from 'app/state/ducks/loan-rollovers/selectors'
import { LoanRolloverParams } from 'app/models/loan-rollover-params'
import {
  loadAllLoanRolloverSplits,
  saveLoanRolloverSplit,
  submitForApprovalSplit,
} from 'app/state/ducks/loan-rollover-splits/thunks'
import { getLoanRolloverSplits } from 'app/state/ducks/loan-rollover-splits/selectors'
import { LoanRolloverSplitParams } from 'app/models/loan-rollover-split-params'
import { DraftLoanParams } from 'app/models/loan-params'
import config from 'app/config/config'
import { loadOneFacility } from 'app/state/ducks/facilities/thunks'
import { ContractPeriod, IroValues } from 'app/models/facility-params'
import axios from 'axios'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { getInterestRatess } from 'app/state/ducks/interest-rates/selectors'
import { loadInterestByIndex } from 'app/state/ducks/interest-rates/thunks'
import { getInterestForPeriod } from 'app/models/interest-rate'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import RolloverFormPresentation from './rollover-form-presentation'
import { getPikConfig, arePikFieldsApplicable } from '../loans/utilities/pik-config-utils'
import { deriveAllInRate } from 'app/utils/rate-rounding'
import { CenteredLoader } from 'app/views/components/loader/loader'
import { usePrerequisiteReads } from 'app/hooks/usePrerequisiteReads'
import { listOf } from 'app/state/utils/load-result'

type Props = {
  close: any
  loan: LoanParams
}

const savedPikRate = (value: unknown): number => {
  // Tolerates the nested-optional form the ledger types declare ([] / [value])
  // and treats absent, empty and unparseable as 0, so a corrupted draft cannot
  // put NaN into form state. Mirrors utils/pik-election.util on the backend.
  const raw = Array.isArray(value) ? value[0] : value
  if (raw == null || raw === '') return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

const hasPikElection = (r?: {
  pikPremium?: unknown
  pikInterestPercent?: unknown
  cashInterestPercent?: unknown
}): boolean =>
  savedPikRate(r?.pikPremium) +
    savedPikRate(r?.pikInterestPercent) +
    savedPikRate(r?.cashInterestPercent) >
  0

export default function RolloverFormLogic({ close, loan }: Props) {
  const dispatch = useDispatch()

  const facility = useSelector(getFacility)
  const loanRollover = useSelector(getLoanRollovers)
  const loanRolloverSplits = useSelector(getLoanRolloverSplits)
  const allIndexes = useSelector(getIndexRateOptions)
  const dailyRates = useSelector(getInterestRatess)
  const businessDate = useSelector(getBusinessDate)

  const pikConfig = getPikConfig(facility?.pikOption)

  const [facilityindexOptions, setFacilityIndexOptions] = useState<Option[]>([])
  const [iro, setIro] = useState<IroValues | undefined>(undefined)
  const [contractPeriod, setContractPeriod] = useState<ContractPeriod>()
  const [isContractPeriodDisabled, setIsContractPeriodDisabled] = useState<boolean>(false)
  const [facilityContractPeriodOptions, setFacilityContractPeriodOptions] =
    useState<SelectItem[]>([])

  // THIS form's read of the loan's rollovers and splits, kept apart from the
  // shared slices: another form's later answer replaces those and would make
  // an existing draft look absent (Save would create a second one). The
  // slice is only the fallback, for a draft saved while the form is open.
  const [own, setOwn] = useState<{ rollovers: LoanRolloverParams[]; splits: LoanRolloverSplitParams[] }>({
    rollovers: [],
    splits: [],
  })
  const rollover =
    own.rollovers.find(lr => lr?.loanId?.id === loan?.id) ??
    loanRollover.find(lr => lr?.loanId?.id === loan?.id) ??
    undefined
  // If a draft split exists for this loan, the user is editing it. The
  // form will load split-specific fields and route Save through the PUT
  // branch of saveLoanRolloverSplit so an edit doesn't create a duplicate.
  const existingSplit =
    own.splits.find(s => s?.loanId?.id === loan?.id) ??
    loanRolloverSplits.find(s => s?.loanId?.id === loan?.id) ??
    undefined

  const calculatePikAmounts = (loan: any): { pikAmount: number, interestPaymentAmount: number, rolloverAmount: number, pikPercentage: number } => {
    const existingLoanHasPik =
      (loan?.pikInterestPercent != null && Number(loan.pikInterestPercent) > 0) ||
      (loan?.pikPremium != null && Number(loan.pikPremium) > 0)

    // For PIK_All and PIK_Scheduled, automatically capitalize accrued interest
    // For PIK_Optional, use the existing loan's PIK status
    const shouldCapitalizeInterest = pikConfig.isPikAll || pikConfig.isPikScheduled ||
      (pikConfig.isPikOptional && existingLoanHasPik)

    // Calculate PIK percentage from existing loan for PIK_Scheduled and PIK_Optional (when PIK is on).
    // PIK_All is intentionally excluded — its capitalization is always 100%
    // by definition (governed by facility.pikOption, not by any loan field).
    // PIK_All loans don't persist pikInterestPercent because the rate is
    // implicit from the facility configuration; deriving from the loan
    // would be redundant.
    let initialPikPercentage = pikConfig.defaultPikPercentage
    const shouldDeriveFromLoan =
      pikConfig.isPikScheduled ||
      (pikConfig.isPikOptional && existingLoanHasPik)
    if (shouldDeriveFromLoan && loan?.pikInterestPercent != null) {
      const allInRate = loan.allInRateWithPikPremium || loan.allInRate
      if (allInRate && allInRate > 0) {
        initialPikPercentage = (loan.pikInterestPercent / allInRate) * 100
      }
    }

    // Calculate PIK amounts based on percentage and accrued interest
    const accruedInterest = Number(loan?.accrualInterestMaturityDate ?? 0.0) + Number(loan?.pikPremiumInterestAtMaturity ?? 0.0)
    const pikAmount = shouldCapitalizeInterest ? accruedInterest * (initialPikPercentage / 100) : 0
    const interestPaymentAmount = shouldCapitalizeInterest ? accruedInterest - pikAmount : 0
    const rolloverAmount = shouldCapitalizeInterest ? Number(loan.amount) + pikAmount : 0.0

    return {
      pikAmount,
      interestPaymentAmount,
      rolloverAmount,
      pikPercentage: initialPikPercentage
    }
  }

  useEffect(() => {
    if (existingSplit) {
      // Editing an existing draft split — pre-populate the form with the
      // saved split's panels (and split-level fields) so the modal opens
      // already in split mode with the user's prior data, not blank
      // defaults. saveLoanRolloverSplit will route to PUT because
      // form.values.id is now set (which makes existingSplit truthy at
      // save time too).
      const hydratedNewLoans = (existingSplit.newLoans ?? []).map(nl => ({
        ...nl,
        // Same string-to-number coercion as the other numeric panel fields
        fxRate: toNumberInputValue(nl.fxRate) || null,
        // The wire shape stores allInRate as the bare rate (without
        // pikPremium). The form's panel state uses the with-pikPremium
        // value (mirrors transformValues at save time). Fall back to
        // allInRate if allInRateWithPikPremium is null (legacy shape).
        allInRate: nl.allInRateWithPikPremium != null
          ? Number(nl.allInRateWithPikPremium)
          : (nl.allInRate != null ? Number(nl.allInRate) : 0),
        // Every numeric panel field must be coerced to Number — DAML
        // serializes Decimal fields as strings, and Mantine NumberInput
        // calls .toFixed() on its value during render, which throws if
        // the value is a string.
        allInRateWithPikPremium: nl.allInRateWithPikPremium != null
          ? Number(nl.allInRateWithPikPremium)
          : 0,
        startDate: nl.startDate ? stringToDate(nl.startDate as any) : null,
        endDate: nl.endDate ? stringToDate(nl.endDate as any) : null,
        amount: nl.amount != null ? Number(nl.amount) : 0,
        amountDue: nl.amountDue != null ? Number(nl.amountDue) : null,
        interestBaseRate: nl.interestBaseRate != null ? Number(nl.interestBaseRate) : 0,
        interestBaseRateWithRounding: nl.interestBaseRateWithRounding != null
          ? Number(nl.interestBaseRateWithRounding)
          : 0,
        margin: nl.margin != null ? Number(nl.margin) : 0,
        casValue: nl.casValue != null ? Number(nl.casValue) : 0,
        pikPremium: nl.pikPremium != null ? Number(nl.pikPremium) : 0,
        pikInterestPercent: nl.pikInterestPercent != null ? Number(nl.pikInterestPercent) : 0,
        cashInterestPercent: nl.cashInterestPercent != null ? Number(nl.cashInterestPercent) : 0,
        // The Index Rate Select is bound to a plain string id (matching
        // facilityindexOptions[].value), but the wire shape stores
        // indexOption as a StableKey { admin, id } — flatten to id so
        // the Select pre-fills correctly on Edit.
        indexOption: typeof nl.indexOption === 'string'
          ? nl.indexOption
          : (nl.indexOption?.id ?? ''),
        // Each panel keeps its DAML id (acts as the React key too).
        // Per-panel newLoanWithPik isn't on DraftLoan, so derive it from
        // whether any PIK rate was persisted on the panel.
        newLoanWithPik: (Number(nl.pikPremium) || 0) > 0
          || (Number(nl.pikInterestPercent) || 0) > 0
          || (Number(nl.cashInterestPercent) || 0) > 0,
      }))
      const formSplit = {
        id: existingSplit.id,
        loanId: existingSplit.loanId,
        currency: existingSplit.currency,
        withPik: existingSplit.withPik ?? false,
        pikPercentage: existingSplit.pikPercentage != null
          ? Number(existingSplit.pikPercentage)
          : 0,
        splitMode: true,
        newLoans: hydratedNewLoans,
        status: existingSplit.status,
      }
      form.setValues(formSplit as any)
    } else if (rollover) {
      const formRollover = {
        ...rollover,
        interestRateOption: rollover.interestRateOption.id,
        allInRate: rollover.allInRateWithPikPremium
          ? Number(rollover.allInRateWithPikPremium)
          : (rollover.allInRate ? Number(rollover.allInRate) + (rollover.pikPremium ? Number(rollover.pikPremium) : 0) : 0),
        amount: Number(rollover.amount),
        pikPercentage: rollover.pikPercentage ? Number(rollover.pikPercentage) : 0.00000,
        interestBaseRate: Number(rollover.interestBaseRate),
        margin: Number(rollover.margin),
        roundedBaseRate: Number(rollover.roundedBaseRate),
        startDate: stringToDate(rollover.startDate),
        maturity: rollover.maturity
          ? stringToDate(rollover.maturity)
          : businessDate,
        // The saved rollover wins: no fallback to the loan's rates, or a
        // rollover with PIK switched off would redisplay the old values.
        // PIK_All / PIK_Scheduled still force the toggle on, since those store
        // the rates as null by design.
        newLoanWithPik: hasPikElection(rollover) || pikConfig.defaultNewLoanPikEnabled,
        pikPremium: savedPikRate(rollover.pikPremium),
        cashInterestPercent: savedPikRate(rollover.cashInterestPercent),
        pikInterestPercent: savedPikRate(rollover.pikInterestPercent),
        allInRateWithPikPremium: rollover.allInRateWithPikPremium
          ? Number(rollover.allInRateWithPikPremium)
          : (rollover.allInRate ? Number(rollover.allInRate) + (rollover.pikPremium ? Number(rollover.pikPremium) : 0) : 0),
        casValue: rollover.casValue ? Number(rollover.casValue) : (loan?.casValue ? Number(loan.casValue) : 0),
      }
      if (rollover.pikPercentage) {
        const totalInterestForRollover = Number(loan?.accrualInterestMaturityDate ?? 0.0) + Number(loan?.pikPremiumInterestAtMaturity ?? 0.0)
        const pikAmount = totalInterestForRollover * (formRollover.pikPercentage / 100)
        const interestPaymentAmount = totalInterestForRollover - pikAmount
        form.setFieldValue('pikAmount', pikAmount)
        form.setFieldValue('interestPaymentAmount', interestPaymentAmount)
        const amount = Number(loan.amount) + pikAmount
        form.setFieldValue('amount', amount)
      }
      form.setValues(formRollover)
    } else {
      const existingLoanHasPik =
        (loan?.pikInterestPercent != null && Number(loan.pikInterestPercent) > 0) ||
        (loan?.pikPremium != null && Number(loan.pikPremium) > 0)

      // For PIK_All and PIK_Scheduled, automatically capitalize accrued interest
      // For PIK_Optional, use the existing loan's PIK status
      const shouldCapitalizeInterest = pikConfig.isPikAll || pikConfig.isPikScheduled ||
        (pikConfig.isPikOptional && existingLoanHasPik)


      const { pikPercentage, pikAmount, interestPaymentAmount, rolloverAmount } = calculatePikAmounts(loan)

      const cleanForm = {
        id: undefined,
        loanId: { admin: loan.agencyAdmin, id: loan.id },
        contractPeriod: null,
        currency: loan.currency,
        withPik: shouldCapitalizeInterest,
        pikPercentage: pikPercentage,
        pikAmount: pikAmount,
        interestPaymentAmount: interestPaymentAmount,
        amount: rolloverAmount,
        startDate: stringToDate(loan.endDate),
        maturity: null,
        interestRateOption: loan.indexOption.id,
        rounding: null,
        dayBasis: null,
        interestBaseRate: '',
        roundedBaseRate: '',
        margin: '',
        allInRate: '',
        status: 'Draft',
        newLoanWithPik: pikConfig.defaultNewLoanPikEnabled,
        pikPremium: 0,
        cashInterestPercent: 0,
        pikInterestPercent: 0,
        allInRateWithPikPremium: '', // Will be calculated by useEffect
        casValue: 0,
      }
      form.setValues(cleanForm)
    }
  }, [rollover, existingSplit])


  useEffect(() => {
    if (!facility) {
      return
    }
    const indexRateOptions = facility.iroValues.find(
      iro => iro.indexOption.id == form.values.interestRateOption
    )
    setIro(indexRateOptions)
    if (!indexRateOptions) {
      return
    }
    if (form.values.rounding === '' || form.values.rounding === null) {
      form.setFieldValue('rounding', indexRateOptions.rounding)
    }
    if (
      form.values.margin === '' ||
      form.values.margin === 0 ||
      form.values.margin === null
    ) {
      form.setFieldValue('margin', Number(indexRateOptions.margin))
    }
    if (form.values.dayBasis === '' || form.values.dayBasis === null) {
      form.setFieldValue('dayBasis', indexRateOptions.dayBasis)
    }
    const selectedContractPeriod = indexRateOptions.contractPeriod.find(
      cp => cp.contractPeriod == form.values.contractPeriod
    )
    setContractPeriod(selectedContractPeriod)
    if (!rollover) {
      calculateInterests(indexRateOptions, selectedContractPeriod, form.values.startDate)
    }
  }, [facility, rollover])

  // The form reads its facility and the loan's existing rollover / split from
  // Redux; load them here so it works wherever it is opened from.
  // The form opens once they have settled: before that the existing draft is
  // unknown and a Save would create a second one. The index rate options are
  // reference data the header loads only when its own form opens.
  const ready = usePrerequisiteReads(
    () => [
      dispatch(loadOneFacility(loan.facilityId.id, 'Approved')),
      dispatch(loadAllLoanRollovers(loan.id)),
      dispatch(loadAllLoanRolloverSplits(loan.id)),
      ...(allIndexes?.length ? [] : [dispatch(loadIndexRateOptions())]),
    ],
    () => {
      // a failed read is not "no draft": opening would let a Save create a
      // second one. Say so and close; the menu can be opened again.
      ErrorNotification({
        title: 'Could not open',
        message: `The loan's facility, its existing rollover or the index rate options could not be loaded. Please try again.`,
      })
      close()
    },
    {
      onLoaded: results =>
        setOwn({
          rollovers: listOf<LoanRolloverParams>(results[1]),
          splits: listOf<LoanRolloverSplitParams>(results[2]),
        }),
    },
  )

  // set interest on edit
  useEffect(() => {
    if (!dailyRates || form.values.interestBaseRate > 0.0) {
      return
    }

    calculateInterests(iro, contractPeriod, form.values.startDate)
  }, [dailyRates, rollover])

  useEffect(() => {
    if (!allIndexes || allIndexes.length == 0 || !facility) {
      return
    }
    const facilityIndexes = [...new Set(facility?.iroValues.map(iro => {
      return allIndexes.find(option => option.id === iro.indexOption.id)
    }))]


    setFacilityIndexOptions(
      facilityIndexes.map(indexOption => ({
        value: indexOption?.id ?? '',
        label: indexOption?.indexOption ?? '',
      })) ?? []
    )


    const selectedIndex = allIndexes.find(
      index => index.id == form.values.interestRateOption
    )
    if (selectedIndex) {
      dispatch(loadInterestByIndex(selectedIndex))
      setIsContractPeriodDisabled(selectedIndex.indexType !== 'TermIndex')
    }
  }, [allIndexes, facility])

  useEffect(() => {
    if (!loan) {
      return
    }
    const filteredIros = facility?.iroValues.find(
      iro =>
        iro.indexOption.id == form.values.interestRateOption &&
        form.values.currency === iro.currency
    )
    if (!filteredIros) {
      return
    }
    const contractPeriods = filteredIros.contractPeriod.map(
      iro => iro.contractPeriod
    )
    const filteredContractPeriodOptions = rateTypeOptions.filter(option =>
      contractPeriods.includes(option.value)
    )
    setFacilityContractPeriodOptions(filteredContractPeriodOptions)
  }, [facility, loan])

  const form: any = useForm({
    initialValues: {
      id: rollover?.id ?? undefined,
      loanId: { admin: loan.agencyAdmin, id: loan.id },
      contractPeriod: rollover?.contractPeriod ?? null,
      currency: rollover?.currency ?? loan.currency,
      // Flat-rollover rate. Splits carry one per new loan instead, so this stays
      // null in split mode. Seeded from the source loan so an unchanged rollover
      // of an FX loan keeps its rate without re-entry.
      fxRate: toNumberInputValue(rollover?.fxRate ?? loan.fxRate) || null,
      amount:
        Number(rollover?.amount) ?? (pikConfig.isPikAll
          ? Number(loan.amount) + Number(loan.accrualInterestMaturityDate)
          : 0.0),
      startDate: rollover?.startDate
        ? stringToDate(rollover?.startDate)
        : stringToDate(loan.endDate),
      maturity: rollover?.maturity ? stringToDate(rollover?.maturity) : null,
      interestRateOption: rollover?.interestRateOption?.id ?? loan.indexOption?.id,
      rounding: rollover?.rounding ?? null,
      dayBasis: rollover?.dayBasis ?? null,
      interestBaseRate: Number(rollover?.interestBaseRate) ?? null,
      roundedBaseRate: Number(rollover?.roundedBaseRate) ?? null,
      margin: Number(rollover?.margin) ?? null,
      allInRate: Number(rollover?.allInRate) ?? null,
      status: rollover?.status ?? 'Draft',
      withPik: rollover?.withPik ?? (
        pikConfig.isPikOptional
          ? ((loan?.pikInterestPercent != null && Number(loan.pikInterestPercent) > 0) ||
            (loan?.pikPremium != null && Number(loan.pikPremium) > 0))
          : pikConfig.isPikAll
      ),
      pikPercentage: rollover?.pikPercentage ? Number(rollover.pikPercentage) : 0.00000,
      // PIK fields are placeholders only. The useEffect below runs on mount with
      // no guard and always calls form.setValues — formRollover when a rollover
      // exists, cleanForm otherwise — so anything derived from `loan` here is
      // overwritten before the user sees it. Kept consistent with
      // cash/pikInterestPercent rather than implying a prefill that never
      // happens; whether a NEW rollover should inherit the loan's PIK rates is
      // a product decision that belongs in cleanForm.
      newLoanWithPik: hasPikElection(rollover) || pikConfig.defaultNewLoanPikEnabled,
      pikPremium: savedPikRate(rollover?.pikPremium),
      cashInterestPercent: savedPikRate(rollover?.cashInterestPercent),
      pikInterestPercent: savedPikRate(rollover?.pikInterestPercent),
      allInRateWithPikPremium: rollover?.allInRateWithPikPremium ? Number(rollover.allInRateWithPikPremium) : 0,
      casValue: rollover?.casValue ? Number(rollover.casValue) : (loan?.casValue ? Number(loan.casValue) : 0),
      hasError: false,
      ErrorMessage: '',
      hasSuccessfulEntitySave: false,
      customer_token: '',
      splitMode: false,
      newLoans: [] as DraftLoanParams[],
    },
    // Returns a different shape than the form values on purpose (e.g.
    // interestRateOption widens from an id to { id, admin }), so the return
    // type is left open rather than tied to the values type.
    transformValues: (values): any => {
      const indexOption = allIndexes.find(
        option => option.id === values.interestRateOption
      )
      const transformed = {
        ...values,
        fxRate: toOptionalFxRate(values.fxRate),
        facilityId: {
          id: facility?.id,
          admin: facility?.accountManagementAdmin,
        },
        rounding: values.rounding != '' ? values.rounding : null,
        dayBasis: values.dayBasis != '' ? values.dayBasis : null,
        interestRateOption: {
          id: indexOption?.id ?? '',
          admin: indexOption?.public ?? ''
        },
        contractPeriod:
          values.contractPeriod != '' ? values.contractPeriod : null,
        allInRate: (values.allInRate != null && values.allInRate !== 0)
          ? Number(values.allInRate) - (Number(values.pikPremium) || 0)
          : null,
        allInRateWithPikPremium: (values.allInRate != null && values.allInRate !== 0)
          ? Number(values.allInRate)
          : null,
      }
      return transformed
    },
    validate: {
      amount: (value: number, values: any) =>
        values?.splitMode ? null : value > 0 ? null : 'Invalid amount',
      // Only checked while the field is visible (rollover currency differs from
      // the facility base currency) and not in split mode, where each panel
      // carries its own rate. The rate is optional — only an entered value has
      // to be positive.
      fxRate: (value: number | null, values: any) => {
        if (values?.splitMode || !facility?.baseCurrency) {
          return null
        }
        if (values.currency === facility.baseCurrency) {
          return null
        }
        return validateOptionalFxRate(value)
      },
      newLoans: {
        fxRate: (value: number | null | undefined, values: any, path: string) => {
          if (!values?.splitMode || !facility?.baseCurrency) {
            return null
          }
          const index = Number(path.split('.')[1])
          const panel = values.newLoans?.[index]
          if (!panel || panel.currency === facility.baseCurrency) {
            return null
          }
          return validateOptionalFxRate(value)
        },
      },
      status: (value: string) =>
        value === 'Draft' ? null : 'Has already been submitted',
      maturity: (value: any, values: any) =>
        values?.splitMode
          ? null
          : isNotEmpty(value)
            ? value > form.values.startDate
              ? null
              : 'Maturity has to be after Start Date'
            : 'Maturity is required',
      startDate: (value: any, values: any) =>
        values?.splitMode
          ? null
          : form.values.maturity > value
            ? null
            : 'Maturity has to be after Start Date',
    },
  })

  const resetInterest = () => {
    form.setFieldValue('interestBaseRate', '')
    form.setFieldValue('roundedBaseRate', '')
    form.setFieldValue('allInRate', '')
    form.clearFieldError('contractPeriod')
    form.clearFieldError('startDate')
  }

  const handleWithPikChange = (event: any) => {
    form.setFieldValue('withPik', event.target.checked)
    if (!event.target.checked) {

      form.setFieldValue('pikPercentage', 0)
      form.setFieldValue('pikAmount', 0)
      form.setFieldValue('interestPaymentAmount', 0)
      form.setFieldValue(
        'amount',
        event.target.checked
          ? Number(loan.amount) + Number(form.values.pikAmount)
          : 0.0
      )
      return
    }
    const { pikAmount, interestPaymentAmount, rolloverAmount, pikPercentage } = calculatePikAmounts(loan)
    form.setFieldValue('pikPercentage', pikPercentage)
    form.setFieldValue('pikAmount', pikAmount)
    form.setFieldValue('interestPaymentAmount', interestPaymentAmount)
    form.setFieldValue('amount', rolloverAmount)
  }

  const changeIndexOption = (event: string | null) => {
    if (!event) {
      return
    }

    const selectedIndex = allIndexes.find(index => index.id == event)
    if (selectedIndex) {
      dispatch(loadInterestByIndex(selectedIndex))
      setIsContractPeriodDisabled(selectedIndex.indexType !== 'TermIndex')
    }
    form.setFieldValue('interestRateOption', event)
    form.setFieldValue('casValue', 0)
    form.setFieldValue('contractPeriod', '')
    setContractPeriod(undefined)

    const filteredIros = facility?.iroValues.find(
      iro => iro.indexOption.id == event && form.values.currency === iro.currency
    )
    if (!filteredIros) {
      form.setFieldValue('rounding', '')
      form.setFieldValue('margin', 0)
      form.setFieldValue('dayBasis', '')
      resetInterest()
      return
    }
    form.setFieldValue('rounding', filteredIros.rounding)
    form.setFieldValue('margin', Number(filteredIros.margin))
    form.setFieldValue('dayBasis', filteredIros.dayBasis)
    setIro(filteredIros)

    const contractPeriods = filteredIros.contractPeriod.map(
      cp => cp.contractPeriod
    )
    const filteredContractPeriodOptions = rateTypeOptions.filter(option =>
      contractPeriods.includes(option.value)
    )
    if (selectedIndex?.indexType !== 'TermIndex') {
      form.setFieldValue('contractPeriod', null)
      form.setFieldValue('endDate', stringToDate((facility?.maturity ?? '')))
      calculateNonTermInterests(filteredIros, form.values.startDate)
    } else {
      setFacilityContractPeriodOptions(filteredContractPeriodOptions)
      resetInterest()
    }
  }

  const changeContractPeriod = (event: string | null) => {
    if (!event) {
      return
    }
    form.setFieldValue('contractPeriod', event)
    if (!iro) {
      return
    }

    const contractWitCass = iro.contractPeriod.find(
      cp => cp.contractPeriod == event
    )
    if (!contractWitCass) {
      return
    }

    setContractPeriod(contractWitCass)
    if (!iro) {
      form.setFieldError(
        'contractPeriod',
        'Not found Index rate option for given contract period'
      )
      return
    }
    form.setFieldValue('casValue', Number(contractWitCass.cas))
    calculateTermInterests(iro, contractWitCass, form.values.startDate)
  }

  const calculateInterests = (
    iro: IroValues | undefined,
    contractPeriodWitCas: ContractPeriod | undefined,
    date: any
  ) => {
    const selectedIndex = allIndexes.find(index => index.id == iro?.indexOption.id)
    if (selectedIndex?.indexType === 'TermIndex') {
      calculateTermInterests(iro, contractPeriodWitCas, date)
      return
    }
    calculateNonTermInterests(iro, date)
  }

  const calculateTermInterests = (
    iro: IroValues | undefined,
    contractPeriodWitCas: ContractPeriod | undefined,
    date: any
  ) => {
    resetInterest()
    if (!iro || !contractPeriodWitCas) {
      return
    }

    const iroSelected = dailyRates.find(
      index => index.effectiveDate === formatDateToUTC(date)
    )

    if (!iroSelected) {
      form.setFieldError(
        'startDate',
        'Not found Index rate option for given start date'
      )
      return
    }

    const interestBaseRate = iroSelected.termRates
      ? getInterestForPeriod(
        iroSelected.termRates,
        contractPeriodWitCas.contractPeriod
      )
      : 0.0
    if (interestBaseRate == null) {
      form.setFieldError(
        'contractPeriod',
        'Not found interest for given contract period'
      )
      form.setFieldError(
        'startDate',
        'Not found Index rate option for given start date'
      )
      return
    }
    const interestWithRounding = Number(
      roundTo(Number(interestBaseRate), iro.rounding) ?? 0.0
    )
    form.setFieldValue('interestBaseRate', Number(interestBaseRate))
    form.setFieldValue('roundedBaseRate', interestWithRounding)
    form.setFieldValue(
      'allInRate',
      interestWithRounding +
      Number(iro.margin) +
      Number(contractPeriodWitCas.cas)
    )
  }

  const createDraftRollover = async () => {
    try {
      const indexOption = allIndexes.find(
        option => option.id === form.values.interestRateOption
      )

      const pikPremium = Number(form.values.pikPremium) || 0
      const allInRateValue = form.values.allInRate != null && form.values.allInRate !== 0
        ? Number(form.values.allInRate)
        : null

      const loanRolloverVals: LoanRolloverParams = {
        ...form.values,
        interestRateOption: {
          id: indexOption?.id ?? '',
          admin: indexOption?.public ?? ''
        },
        maturity: formatDateToUTC(form.values.maturity),
        startDate: formatDateToUTC(form.values.startDate),
        allInRate: allInRateValue !== null ? allInRateValue - pikPremium : null,
        allInRateWithPikPremium: allInRateValue,
        pikPremium: arePikFieldsApplicable(facility?.pikOption, form.values.newLoanWithPik === true)
          ? (form.values.pikPremium != null ? Number(form.values.pikPremium) : null)
          : null,
        pikInterestPercent: arePikFieldsApplicable(facility?.pikOption, form.values.newLoanWithPik === true)
          ? Number(form.values.pikInterestPercent || 0)
          : null,
        cashInterestPercent: arePikFieldsApplicable(facility?.pikOption, form.values.newLoanWithPik === true)
          ? Number(form.values.cashInterestPercent || 0)
          : null,
        hasError: false,
        hasSuccessfulEntitySave: false,
        ErrorMessage: '',
        customer_token: config.company.toUpperCase(),
      }

      const response: any = await dispatch(
        saveLoanRollover(loanRolloverVals, rollover)
      )
      if (response.success) {
        SuccessNotification({
          title: 'Successfully Saved Draft Loan Rollover',
          message: 'You created or edited a Loan Rollover',
        })
      } else {
        ErrorNotification({
          title: 'Failed to Create Rollover',
          message: response.payload ?? 'Please check inputs',
        })
      }
      return response
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status == 400) {
        const data: any = error.response.data
        ErrorNotification({
          title: 'Failed to Create Rollover',
          message: data?.error ?? 'Please check inputs',
        })
        return null
      } else {
        ErrorNotification({
          title: 'Failed to Create Rollover',
          message: error,
        })
        return null
      }
    } finally {
      form.reset()
    }
  }


  function calculateNonTermInterests(
    iro: IroValues | undefined,
    date: string,
  ) {
    resetInterest();
    if (!iro) {
      return;
    }

    const indexRateSelected = allIndexes.find(index => index.id === iro.indexOption.id);
    const startDate = date;
    dailyRates.sort((a, b) => moment(a.effectiveDate).diff(moment(b.effectiveDate)));

    const iroSelected = [...dailyRates].reverse().find(rate => moment(rate.effectiveDate).isBefore(startDate));

    if (!iroSelected && indexRateSelected?.indexType !== 'FixedIndex') {
      form.setFieldError('indexOption', 'Not found Index rate option for given index option');
      return;
    }

    let interestBaseRate = ''
    if (indexRateSelected?.indexType === 'FixedIndex') {
      interestBaseRate = iro.fixedRate + ''
    }
    else if (iroSelected?.overnightRate) {
      interestBaseRate = iroSelected.overnightRate
    }
    else if (iroSelected?.floatingRate) {
      interestBaseRate = iroSelected.floatingRate
    }

    const interestWithRounding = roundTo(Number(interestBaseRate), iro.rounding ?? 0) ?? 0

    form.setFieldValue('interestBaseRate', Number(interestBaseRate));
    form.setFieldValue('roundedBaseRate', Number(interestWithRounding));
    form.setFieldValue('allInRate', Number(interestWithRounding) + (Number(iro.margin) ?? 0))
  };

  function onInterestBaseRateChange(value: number | ''): void {
    form.setFieldValue('interestBaseRate', value)
    const { roundedBaseRate, allInRate } = deriveAllInRate({
      interestBaseRate: value,
      rounding: form.values.rounding,
      margin: form.values.margin,
      casValue: form.values.casValue,
    })
    form.setFieldValue('roundedBaseRate', roundedBaseRate)
    form.setFieldValue('allInRate', allInRate)
  }

  function onMarginChange(value: number | ''): void {
    form.setFieldValue('margin', value)
    form.setFieldValue(
      'allInRate',
      deriveAllInRate({
        interestBaseRate: form.values.interestBaseRate,
        rounding: form.values.rounding,
        margin: value,
        casValue: form.values.casValue,
      }).allInRate
    )
  }

  // --- Split-mode helpers ---

  const buildNewLoanDefaults = (): DraftLoanParams => {
    // Each panel starts fresh — inherit the facility/loan context the way the
    // non-split rollover form does at open time, but without carrying over any
    // values the user may have typed on the flat form. Rate-entry fields
    // (interestBaseRate, interestBaseRateWithRounding, allInRate) start at 0;
    // contractPeriod/maturity start empty — the user picks them per panel.
    const selectedIndex = allIndexes.find(
      option => option.id === loan.indexOption?.id,
    )
    const matchingIro = facility?.iroValues.find(
      iro => iro.indexOption.id === loan.indexOption?.id,
    )

    return {
      // Stable per-panel id used as the React key in the split-loan-panel
      // list. Persists with the panel through reorder / removal so unrelated
      // panels' local UI state (date picker open, focused field) survives.
      id: uuidv4(),
      facilityId: loan.facilityId,
      amount: 0,
      currency: loan.currency,
      // Each split panel needs its own rate: the ledger reads fxRate off every
      // new loan, not off the split. Seeded from the source loan.
      fxRate: toNumberInputValue(loan.fxRate) || null,
      dayBasis: matchingIro?.dayBasis ?? null,
      interestBaseRate: 0,
      interestBaseRateWithRounding: 0,
      rounding: matchingIro?.rounding ?? null,
      endDate: null,
      startDate: stringToDate(loan.endDate) as any,
      isSwingLine: false,
      isNonProRata: false,
      margin: Number(matchingIro?.margin) || 0,
      allInRate: 0,
      indexOption: (selectedIndex?.id ?? loan.indexOption?.id ?? '') as any,
      casValue: 0,
      borrowerPaymentInstructions: loan.borrowerPaymentInstructions ?? null,
      borrower: loan.borrower,
      lendersPositions: [],
      contractPeriod: '',
      agencyAdmin: loan.agencyAdmin,
      agencyManager: loan.agencyManager,
      nextInterestPaymentDate: null,
      pikPremium: 0,
      pikInterestPercent: 0,
      cashInterestPercent: 0,
      allInRateWithPikPremium: 0,
    }
  }

  const toggleSplitMode = (checked: boolean) => {
    form.setFieldValue('splitMode', checked)
    if (checked) {
      // Seed the first panel from every new-loan field the user has entered
      const base = buildNewLoanDefaults()
      const seed: DraftLoanParams = {
        ...base,
        amount: Number(form.values.amount) || 0,
        // Carry over a rate already entered on the flat form
        fxRate: toNumberInputValue(form.values.fxRate ?? base.fxRate) || null,
        startDate: (form.values.startDate ?? base.startDate) as any,
        endDate: (form.values.maturity ?? null) as any,
        indexOption: (form.values.interestRateOption ?? base.indexOption) as any,
        contractPeriod: form.values.contractPeriod ?? '',
        rounding: form.values.rounding ?? base.rounding,
        dayBasis: form.values.dayBasis ?? base.dayBasis,
        interestBaseRate: Number(form.values.interestBaseRate) || 0,
        interestBaseRateWithRounding: Number(form.values.roundedBaseRate) || 0,
        margin: Number(form.values.margin) || 0,
        allInRate: Number(form.values.allInRate) || 0,
        allInRateWithPikPremium:
          Number(form.values.allInRateWithPikPremium) || 0,
        casValue: Number(form.values.casValue) || 0,
        pikPremium: Number(form.values.pikPremium) || 0,
        pikInterestPercent: Number(form.values.pikInterestPercent) || 0,
        cashInterestPercent: Number(form.values.cashInterestPercent) || 0,
      }
      ;(seed as any).newLoanWithPik = form.values.newLoanWithPik === true
      const second = buildNewLoanDefaults()
      form.setFieldValue('newLoans', [seed, second])
    } else {
      form.setFieldValue('newLoans', [])
    }
  }

  const addSplitPanel = () => {
    const existing = (form.values.newLoans ?? []) as DraftLoanParams[]
    form.setFieldValue('newLoans', [...existing, buildNewLoanDefaults()])
  }

  const removeSplitPanel = (index: number) => {
    const existing = (form.values.newLoans ?? []) as DraftLoanParams[]
    if (existing.length <= 1) return
    const updated = [...existing]
    updated.splice(index, 1)
    form.setFieldValue('newLoans', updated)
  }

  const createDraftRolloverSplit = async () => {
    try {
      const newLoansPayload: DraftLoanParams[] = (form.values.newLoans ?? []).map(
        (nl: DraftLoanParams) => {
          const panelIndexId =
            typeof nl.indexOption === 'string'
              ? (nl.indexOption as string)
              : (nl.indexOption?.id ?? '')
          const matchedIndex = allIndexes.find(
            option => option.id === panelIndexId,
          )
          return {
            ...nl,
            amount: Number(nl.amount) || 0,
            startDate: nl.startDate
              ? formatDateToUTC(nl.startDate as any)
              : null,
            endDate: nl.endDate ? formatDateToUTC(nl.endDate as any) : null,
            interestBaseRate:
              nl.interestBaseRate != null ? Number(nl.interestBaseRate) : null,
            interestBaseRateWithRounding:
              nl.interestBaseRateWithRounding != null
                ? Number(nl.interestBaseRateWithRounding)
                : null,
            margin: nl.margin != null ? Number(nl.margin) : null,
            // Panel state holds the UI-displayed All-In Rate (base + margin +
            // cas + pikPremium). Split it back out at save time: the ledger's
            // `allInRate` is the rate WITHOUT PIK Premium, and
            // `allInRateWithPikPremium` holds the full displayed rate. Mirrors
            // transformValues in the non-split rollover form.
            allInRate:
              nl.allInRate != null && Number(nl.allInRate) !== 0
                ? Number(nl.allInRate) - (Number(nl.pikPremium) || 0)
                : null,
            allInRateWithPikPremium:
              nl.allInRate != null && Number(nl.allInRate) !== 0
                ? Number(nl.allInRate)
                : null,
            casValue: nl.casValue ?? 0,
            fxRate: toOptionalFxRate(nl.fxRate),
            pikPremium: nl.pikPremium != null ? Number(nl.pikPremium) : null,
            pikInterestPercent:
              nl.pikInterestPercent != null
                ? Number(nl.pikInterestPercent)
                : null,
            cashInterestPercent:
              nl.cashInterestPercent != null
                ? Number(nl.cashInterestPercent)
                : null,
            indexOption: {
              id: matchedIndex?.id ?? panelIndexId,
              admin: matchedIndex?.public ?? loan.indexOption?.admin ?? '',
            },
          }
        },
      )

      const splitPayload: LoanRolloverSplitParams = {
        id: form.values.id,
        agencyAdmin: loan.agencyAdmin,
        agencyManager: loan.agencyManager,
        loanId: { admin: loan.agencyAdmin, id: loan.id ?? '' },
        currency: form.values.currency ?? loan.currency,
        withPik: form.values.withPik ?? false,
        pikPercentage:
          form.values.withPik === true
            ? Number(form.values.pikPercentage) || 0
            : null,
        pikPremium: null,
        cashInterestPercent: null,
        pikInterestPercent: null,
        allInRateWithPikPremium: null,
        newLoans: newLoansPayload,
        observers: [],
        status: 'Draft',
        hasError: false,
        ErrorMessage: '',
        hasSuccessfulEntitySave: false,
        customer_token: config.company.toUpperCase(),
      }

      const response: any = await dispatch(
        saveLoanRolloverSplit(splitPayload, existingSplit),
      )
      if (response.success) {
        SuccessNotification({
          title: 'Successfully Saved Draft Loan Rollover Split',
          message: 'You created or edited a Loan Rollover Split',
        })
      } else {
        ErrorNotification({
          title: 'Failed to Create Rollover Split',
          message: response.payload ?? 'Please check inputs',
        })
      }
      return response
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status == 400) {
        const data: any = error.response.data
        ErrorNotification({
          title: 'Failed to Create Rollover Split',
          message: data?.error ?? 'Please check inputs',
        })
        return null
      }
      ErrorNotification({
        title: 'Failed to Create Rollover Split',
        message: error,
      })
      return null
    } finally {
      form.reset()
    }
  }

  // `splitModeHint` is passed explicitly by the caller because
  // createDraftRollover/Split reset the form (and thus form.values.splitMode)
  // inside their `finally` block before this runs.
  const submitDraftForApproval = async (draft: any, splitModeHint?: boolean) => {
    const isSplit = splitModeHint ?? form.values.splitMode
    if (!draft) {
      ErrorNotification({
        title: 'Could not submit',
        message: 'Draft was not created; nothing to submit.',
      })
      return { success: false, payload: 'Draft missing' }
    }
    if (isSplit) {
      return dispatch(submitForApprovalSplit(draft))
    }
    return dispatch(submitForApproval(draft))
  }

  // the saved record decides create vs edit: nothing is editable until it is known
  if (!ready) return <CenteredLoader />

  return (
    <RolloverFormPresentation
      close={close}
      form={form}
      facility={facility}
      loan={loan}
      rollover={rollover}
      existingSplit={existingSplit}
      iro={iro}
      contractPeriod={contractPeriod}
      calculateInterests={calculateInterests}
      facilityindexOptions={facilityindexOptions}
      facilityContractPeriodOptions={facilityContractPeriodOptions}
      changeIndexOption={changeIndexOption}
      handleWithPikChange={handleWithPikChange}
      changeContractPeriod={changeContractPeriod}
      onInterestBaseRateChange={onInterestBaseRateChange}
      createDraftRollover={createDraftRollover}
      createDraftRolloverSplit={createDraftRolloverSplit}
      submitDraftForApproval={submitDraftForApproval}
      toggleSplitMode={toggleSplitMode}
      addSplitPanel={addSplitPanel}
      removeSplitPanel={removeSplitPanel}
      onMarginChange={onMarginChange}
      isContractPeriodDisabled={isContractPeriodDisabled}
      pikConfig={pikConfig}
    />
  )
}